import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Check, PenLine, ShieldCheck, FileText, BookOpen } from "lucide-react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";

/**
 * Introducer portal dashboard.
 *
 * Shows only aggregate KPIs — the introducer NEVER sees names, emails or KYC
 * data of referred clients (that would violate GDPR & MiFID II duty of care).
 */
export default function IntroducerDashboardPage() {
  const { locale, t } = useI18n();
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .get("/introducer/me")
      .then(({ data }) => setMe(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const copyLink = async () => {
    if (!me?.referral_link) return;
    try {
      await navigator.clipboard.writeText(me.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Fallback: create a hidden textarea (older mobile browsers)
      const el = document.createElement("textarea");
      el.value = me.referral_link;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
      } catch (execErr) {
        console.warn("[introducer] copy fallback failed:", execErr?.message || execErr);
      }
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  const fmt = (n) =>
    new Intl.NumberFormat(locale === "en" ? "en-GB" : locale === "de" ? "de-DE" : locale === "no" ? "nb-NO" : "it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  return (
    <PortalLayout>
      <section
        className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8"
        data-testid="introducer-dashboard-page"
      >
        <div className="br-fade-up">
          <div className="br-overline">{t("intro.dashboard_overline")}</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            {t("intro.dashboard_heading_a")}{" "}
            <em>{me?.name || "…"}</em>
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[720px]">
            {t("intro.dashboard_intro")}
          </p>
        </div>

        {error && (
          <div className="mt-6 br-card p-4 text-[color:var(--br-red)]" data-testid="intro-error">
            {error}
          </div>
        )}

        {me && (
          <>
            {/* Contract signature banner */}
            {!me.contract_signed ? (
              <Link
                to="/introducer/contract"
                className="mt-8 br-card px-8 py-6 flex flex-wrap justify-between items-center gap-4 group cursor-pointer transition-colors"
                style={{ borderLeft: "3px solid var(--br-red)", background: "rgba(179,107,107,0.05)" }}
                data-testid="contract-banner-pending"
              >
                <div>
                  <div className="br-overline flex items-center gap-2" style={{ color: "var(--br-red)" }}>
                    <PenLine size={14} /> {t("intro.contract_banner_overline_pending")}
                  </div>
                  <h2 className="font-serif-display text-[1.5rem] mt-2">
                    {t("intro.contract_banner_title_pending")}
                  </h2>
                  <p className="text-[0.9rem] text-[color:var(--br-text-dim)] mt-2 max-w-[560px]">
                    {t("intro.contract_banner_body_pending")}
                  </p>
                </div>
                <span className="br-btn-primary" style={{ padding: "10px 22px" }}>
                  {t("intro.contract_banner_cta_pending")} →
                </span>
              </Link>
            ) : (
              <div
                className="mt-8 br-card px-8 py-5 flex flex-wrap justify-between items-center gap-4"
                style={{ borderLeft: "3px solid var(--br-bronze-soft)" }}
                data-testid="contract-banner-signed"
              >
                <div>
                  <div className="br-overline flex items-center gap-2" style={{ color: "var(--br-bronze-soft)" }}>
                    <ShieldCheck size={14} /> {t("intro.contract_banner_overline_signed")}
                  </div>
                  <p className="text-[0.9rem] text-[color:var(--br-text)] mt-2">
                    {t("intro.contract_banner_body_signed", { date: me.contract_signed_at || "—" })}
                  </p>
                </div>
                <Link
                  to="/introducer/contract"
                  className="br-btn-outline"
                  style={{ padding: "8px 16px", fontSize: "0.72rem" }}
                >
                  {t("intro.contract_banner_view_signed")}
                </Link>
              </div>
            )}

            {/* KPI row */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi
                label={t("intro.kpi_referrals_total")}
                value={String(me.stats.referrals_total)}
                sublabel={t("intro.kpi_referrals_active_sub", { count: me.stats.referrals_active })}
                testid="kpi-referrals"
              />
              <Kpi
                label={t("intro.kpi_aum")}
                value={fmt(me.stats.aum_referred)}
                sublabel={t("intro.kpi_deposits_count", { count: me.stats.deposits_count })}
                testid="kpi-aum"
              />
              <Kpi
                label={t("intro.kpi_accrued")}
                value={fmt(me.stats.accrued_net)}
                sublabel={t("intro.kpi_accrued_sub", {
                  gross: fmt(me.stats.accrued_gross),
                })}
                testid="kpi-accrued"
              />
              <Kpi
                label={t("intro.kpi_yearly_estimate")}
                value={fmt(me.stats.yearly_estimate_net)}
                sublabel={t("intro.kpi_commission_rate", {
                  pct: me.commission_pct_annual.toFixed(2),
                })}
                testid="kpi-yearly"
              />
            </div>

            {/* Share link + QR */}
            <div className="mt-10 br-card br-aurora-ribbon px-8 py-8" data-testid="share-card">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-center">
                <div>
                  <div className="br-overline">{t("intro.share_overline")}</div>
                  <h2 className="font-serif-display text-[1.8rem] mt-3">
                    {t("intro.share_heading")}
                  </h2>
                  <p className="text-[0.95rem] text-[color:var(--br-text)] mt-3 leading-relaxed">
                    {t("intro.share_body")}
                  </p>
                  <div
                    className="mt-6 p-4 border font-mono-fin text-[0.85rem] tracking-[0.06em] break-all"
                    style={{ borderColor: "var(--br-line-bright)", color: "var(--br-parchment)", background: "rgba(255,255,255,0.02)" }}
                    data-testid="referral-link-display"
                  >
                    {me.referral_link}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={copyLink}
                      className="br-btn-primary inline-flex items-center gap-2"
                      style={{ padding: "10px 20px" }}
                      data-testid="copy-link-btn"
                    >
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                      {copied ? t("intro.copied") : t("intro.copy_link")}
                    </button>
                    <div
                      className="font-mono-fin text-[0.75rem] tracking-[0.24em] uppercase text-[color:var(--br-bronze)] px-4 py-3 border"
                      style={{ borderColor: "var(--br-line)" }}
                      data-testid="promo-code-badge"
                    >
                      {t("intro.code_label")}: {me.code}
                    </div>
                  </div>
                </div>

                <div
                  className="p-4 bg-white flex items-center justify-center"
                  style={{ borderRadius: 8, minWidth: 172 }}
                  data-testid="qr-container"
                >
                  <QRCodeCanvas
                    value={me.referral_link}
                    size={148}
                    bgColor="#ffffff"
                    fgColor="#0A0E1A"
                    includeMargin={false}
                    level="M"
                  />
                </div>
              </div>
            </div>

            {/* Privacy explainer */}
            <div className="mt-10 br-card px-8 py-6" data-testid="privacy-explainer">
              <div className="br-overline">{t("intro.privacy_overline")}</div>
              <p className="text-[0.9rem] text-[color:var(--br-text-dim)] mt-3 leading-relaxed">
                {t("intro.privacy_body")}
              </p>
            </div>

            {/* Statements CTA (only visible after contract signed) */}
            {me.contract_signed && (
              <Link
                to="/introducer/statements"
                className="mt-6 br-card px-8 py-6 flex flex-wrap justify-between items-center gap-4 group transition-colors"
                style={{ borderLeft: "3px solid var(--br-bronze-soft)" }}
                data-testid="statements-cta-card"
              >
                <div>
                  <div className="br-overline flex items-center gap-2" style={{ color: "var(--br-bronze-soft)" }}>
                    <FileText size={14} /> {t("intro.dashboard_view_statements")}
                  </div>
                  <p className="text-[0.9rem] text-[color:var(--br-text)] mt-2">
                    {t("intro.dashboard_view_statements_sub")}
                  </p>
                </div>
                <span className="br-btn-outline" style={{ padding: "8px 16px", fontSize: "0.72rem" }}>
                  {t("intro.dashboard_view_statements")} →
                </span>
              </Link>
            )}

            {/* Partner Playbook CTA (always visible) */}
            <Link
              to="/introducer/guide"
              className="mt-4 br-card px-8 py-6 flex flex-wrap justify-between items-center gap-4 group transition-colors"
              style={{ borderLeft: "3px solid var(--br-bronze)" }}
              data-testid="playbook-cta-card"
            >
              <div>
                <div className="br-overline flex items-center gap-2" style={{ color: "var(--br-bronze)" }}>
                  <BookOpen size={14} /> {t("intro.dashboard_playbook")}
                </div>
                <p className="text-[0.9rem] text-[color:var(--br-text)] mt-2">
                  {t("intro.dashboard_playbook_sub")}
                </p>
              </div>
              <span className="br-btn-outline" style={{ padding: "8px 16px", fontSize: "0.72rem" }}>
                {t("intro.dashboard_playbook_cta")} →
              </span>
            </Link>
          </>
        )}
      </section>
    </PortalLayout>
  );
}

function Kpi({ label, value, sublabel, testid }) {
  return (
    <div className="br-card px-5 py-6" data-testid={testid}>
      <div className="font-mono-fin text-[0.6rem] tracking-[0.24em] uppercase text-[color:var(--br-bronze)]">
        {label}
      </div>
      <div className="font-serif-display text-[2rem] mt-2 leading-none text-[color:var(--br-parchment)]">
        {value}
      </div>
      {sublabel && (
        <div className="mt-2 text-[0.75rem] text-[color:var(--br-text-dim)]">{sublabel}</div>
      )}
    </div>
  );
}
